import type { ProcedureUtils } from "@orpc/tanstack-query";
import type { QueryKey } from "@tanstack/react-query";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { orpc } from "@/client";
import { notifyError } from "@/lib/error-code";
import { MedicoLabels } from "@/lib/labels/medico";

function useInvalidate(extra?: QueryKey) {
	const queryClient = useQueryClient();

	return () => {
		void queryClient.invalidateQueries({ queryKey: orpc.medico.key() });
		void queryClient.invalidateQueries({ queryKey: orpc.clinica.key() });

		if (extra) {
			void queryClient.invalidateQueries({ queryKey: extra });
		}
	};
}

const onError = notifyError(MedicoLabels.error);

function useMedicoMutation<TInput, TOutput, TError>(
	procedure: ProcedureUtils<Record<never, never>, TInput, TOutput, TError>,
	extraKey?: QueryKey,
) {
	const invalidate = useInvalidate(extraKey);

	return useMutation(procedure.mutationOptions({ onSuccess: invalidate, onError }));
}

export function useMedicoSync() {
	const invalidate = useInvalidate();

	return useMutation(
		orpc.medico.sync.mutationOptions({
			onSuccess: (result) => {
				toast.success(MedicoLabels.syncToast(result));
				invalidate();
			},
			onError,
		}),
	);
}

export const useMedicoLink = () => useMedicoMutation(orpc.medico.link);

export const useMedicoPaste = () => useMedicoMutation(orpc.medico.paste);

export const useMedicoIgnore = () => useMedicoMutation(orpc.medico.ignore);

export const useMedicoUnignore = () => useMedicoMutation(orpc.medico.unignore);

export const useMedicoUnlink = () => useMedicoMutation(orpc.medico.unlink, orpc.manuals.key());

export const useMedicoPartialUnlink = () =>
	useMedicoMutation(orpc.medico.partialUnlink, orpc.manuals.key());

export const useMedicoRelink = () => useMedicoMutation(orpc.medico.relink);

export const useMedicoRequeue = () => useMedicoMutation(orpc.medico.requeueJob);

export const useMedicoDiscardJob = () => useMedicoMutation(orpc.medico.discardJob);

export const useMedicoRetryJob = () => useMedicoMutation(orpc.medico.retryJob);

export const useMedicoRetryStage = () => useMedicoMutation(orpc.medico.retryStage);

export const useMedicoEditSection = () => useMedicoMutation(orpc.medico.editSection);

export const useMedicoValidateSection = () => useMedicoMutation(orpc.medico.validateSection);

export function useProntuario(medPatientId: number) {
	return useSuspenseQuery(orpc.medico.prontuario.queryOptions({ input: { medPatientId } }));
}
